'use client'

import React, { useState, useMemo, useCallback } from 'react'
import {
  JobBoardVolunteer,
  JobBoardGroup,
  JobBoardTeam,
} from '../../api/work-orders.api'
import { usePriorityData } from '@/features/priority/hooks/use-priority-data'
import { setActiveJobBoardDrag } from '../../constants/job-board-dnd'
import { Input } from '@/shared/components/ui/input'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import {
  Search,
  ChevronDown,
  Calendar,
  HardHat,
  Sparkles,
  UserCheck,
  ChefHat,
  UtensilsCrossed,
  Users,
  Plus,
  CheckCircle2,
  AlertTriangle,
  Layers,
  Filter,
  RefreshCw,
  MapPin,
  X,
  Check,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'

// Mapping from work scope key/slug to skill checker and human display
interface ScopeSkillDefinition {
  slugs: string[]
  displayName: string
  iconName: string
  matcher: (vol: JobBoardVolunteer) => { matches: boolean; rating: number; skillLabel: string }
}

const SCOPE_SKILL_DEFINITIONS: Record<string, ScopeSkillDefinition> = {
  roofing: {
    slugs: ['tarp-roof', 'install-roofing', 'roofing'],
    displayName: 'Roofing & Tarp',
    iconName: 'roofing',
    matcher: (vol) => {
      const r = vol.skills?.roofing ?? 0
      return { matches: r >= 3, rating: r, skillLabel: `Roofing (${r}/5)` }
    },
  },
  treeWork: {
    slugs: ['tree-work', 'arborist', 'heavy-equipment-demo'],
    displayName: 'Tree Work / Demo',
    iconName: 'arborist',
    matcher: (vol) => {
      const arb = vol.skills?.arborist ?? 0
      const hvy = vol.skills?.heavyEquipment ?? 0
      const best = Math.max(arb, hvy)
      return {
        matches: best >= 3,
        rating: best,
        skillLabel: arb >= hvy ? `Tree Work (${arb}/5)` : `Heavy Eq (${hvy}/5)`,
      }
    },
  },
  drywall: {
    slugs: ['drywall-hanging', 'mud-and-tape', 'drywall'],
    displayName: 'Drywall & Mud',
    iconName: 'drywall',
    matcher: (vol) => {
      const r = vol.skills?.drywallFinishing ?? 0
      return { matches: r >= 3, rating: r, skillLabel: `Drywall (${r}/5)` }
    },
  },
  carpentry: {
    slugs: ['rough-carpentry', 'finish-carpentry', 'trim-install', 'rebuild-prep'],
    displayName: 'Carpentry',
    iconName: 'carpentry',
    matcher: (vol) => {
      const rc = vol.skills?.roughCarpentry ?? 0
      const fc = vol.skills?.finishCarpentry ?? 0
      const best = Math.max(rc, fc)
      return {
        matches: best >= 3,
        rating: best,
        skillLabel: rc >= fc ? `Rough Carpentry (${rc}/5)` : `Finish Carpentry (${fc}/5)`,
      }
    },
  },
  flooring: {
    slugs: ['flooring-install', 'flooring'],
    displayName: 'Flooring',
    iconName: 'flooring',
    matcher: (vol) => {
      const r = vol.skills?.flooring ?? 0
      return { matches: r >= 3, rating: r, skillLabel: `Flooring (${r}/5)` }
    },
  },
  electrical: {
    slugs: ['electrical-fixtures', 'electrical'],
    displayName: 'Electrical',
    iconName: 'electrical',
    matcher: (vol) => {
      const r = vol.skills?.electrical ?? 0
      return { matches: r >= 3, rating: r, skillLabel: `Electrical (${r}/5)` }
    },
  },
  plumbing: {
    slugs: ['plumbing-fixtures', 'plumbing'],
    displayName: 'Plumbing',
    iconName: 'plumbing',
    matcher: (vol) => {
      const r = vol.skills?.plumbing ?? 0
      return { matches: r >= 3, rating: r, skillLabel: `Plumbing (${r}/5)` }
    },
  },
  hvac: {
    slugs: ['hvac-final', 'hvac'],
    displayName: 'HVAC',
    iconName: 'hvac',
    matcher: (vol) => {
      const r = vol.skills?.hvac ?? 0
      return { matches: r >= 3, rating: r, skillLabel: `HVAC (${r}/5)` }
    },
  },
  muckGut: {
    slugs: ['muck-and-gut-interior', 'exterior-debris-removal', 'mold-remediation'],
    displayName: 'Muck, Gut & Debris',
    iconName: 'manualLabor',
    matcher: (vol) => {
      const ml = vol.skills?.manualLabor ?? 0
      const gc = vol.experienceConstruction ?? 0
      const best = Math.max(ml, gc)
      return {
        matches: ml >= 1 || gc >= 2,
        rating: best,
        skillLabel: ml >= 1 ? `Manual Labor (${ml}/5)` : `Gen Const (${gc}/5)`,
      }
    },
  },
  assessment: {
    slugs: ['assess-needs'],
    displayName: 'Assessment',
    iconName: 'leadTeams',
    matcher: (vol) => {
      const lt = vol.experienceLeadingTeams ?? 0
      const gc = vol.experienceConstruction ?? 0
      const best = Math.max(lt, gc)
      return {
        matches: best >= 3,
        rating: best,
        skillLabel: lt >= 3 ? `Team Lead (${lt}/5)` : `Const (${gc}/5)`,
      }
    },
  },
}

function matchVolunteersForScope(
  scopeKey: string,
  volunteers: JobBoardVolunteer[]
): Array<{ volunteer: JobBoardVolunteer; rating: number; skillLabel: string }> {
  const normKey = scopeKey.toLowerCase()
  let matchedDef: ScopeSkillDefinition | undefined

  for (const def of Object.values(SCOPE_SKILL_DEFINITIONS)) {
    if (def.slugs.some((s) => normKey.includes(s) || s.includes(normKey))) {
      matchedDef = def
      break
    }
  }

  if (!matchedDef) {
    // Default fallback: any general construction >= 3
    return volunteers
      .filter((v) => (v.experienceConstruction ?? 0) >= 3)
      .map((v) => ({
        volunteer: v,
        rating: v.experienceConstruction ?? 0,
        skillLabel: `Gen Const (${v.experienceConstruction}/5)`,
      }))
      .sort((a, b) => b.rating - a.rating)
  }

  const matches: Array<{ volunteer: JobBoardVolunteer; rating: number; skillLabel: string }> = []
  volunteers.forEach((v) => {
    const res = matchedDef!.matcher(v)
    if (res.matches) {
      matches.push({
        volunteer: v,
        rating: res.rating,
        skillLabel: res.skillLabel,
      })
    }
  })

  return matches.sort((a, b) => b.rating - a.rating)
}

interface VolunteerOverviewCardProps {
  title: string
  count: number
  countNode?: React.ReactNode
  icon: React.ReactNode
  highlight?: 'construction' | 'other' | 'info' | 'neutral'
  users: JobBoardVolunteer[]
  sections?: Array<{ label: string; users: JobBoardVolunteer[] }>
  onSelectUser?: (user: JobBoardVolunteer) => void
  userSubtitle?: (user: JobBoardVolunteer) => string
}

function VolunteerOverviewCard({
  title,
  count,
  countNode,
  icon,
  highlight,
  users,
  sections,
  onSelectUser,
  userSubtitle,
}: VolunteerOverviewCardProps) {
  const cardHighlightClass =
    highlight === 'construction'
      ? 'bg-emerald-50/70 border-emerald-200/70 dark:bg-emerald-950/25 dark:border-emerald-900/50'
      : highlight === 'other'
        ? 'bg-amber-50/70 border-amber-200/70 dark:bg-amber-950/25 dark:border-amber-900/50'
        : highlight === 'info'
          ? 'bg-sky-50/70 border-sky-200/70 dark:bg-sky-950/25 dark:border-sky-900/50'
          : highlight === 'neutral'
            ? 'bg-slate-50/70 border-slate-200/70 dark:bg-slate-900/25 dark:border-slate-800/60'
            : 'bg-background border-border'

  const iconHighlightClass =
    highlight === 'construction'
      ? 'bg-emerald-100/80 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
      : highlight === 'other'
        ? 'bg-amber-100/80 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
        : highlight === 'info'
          ? 'bg-sky-100/80 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300'
          : highlight === 'neutral'
            ? 'bg-slate-100/80 text-slate-700 dark:bg-slate-800/50 dark:text-slate-300'
            : 'bg-primary/10 text-primary'

  const renderDropdownContent = () => {
    if (sections && sections.length > 0) {
      const hasAny = sections.some((s) => s.users.length > 0)
      if (!hasAny) {
        return <div className="px-3 py-3 text-xs text-muted-foreground">No volunteers</div>
      }
      return (
        <>
          {sections.map((section, idx) => (
            <React.Fragment key={section.label}>
              {idx > 0 && <DropdownMenuSeparator />}
              <DropdownMenuLabel className="text-muted-foreground text-xs font-bold">
                {section.label} ({section.users.length})
              </DropdownMenuLabel>
              {section.users.length === 0 ? (
                <div className="px-2 py-1 text-xs text-muted-foreground">None</div>
              ) : (
                section.users.map((u) => (
                  <DropdownMenuItem
                    key={u.userId}
                    onClick={() => onSelectUser?.(u)}
                    className="flex flex-col items-start py-1.5 cursor-pointer text-xs"
                  >
                    <span className="font-medium text-foreground">{u.fullName}</span>
                    <span className="text-[11px] text-muted-foreground">
                      {userSubtitle ? userSubtitle(u) : u.groupName || 'Individual Volunteer'}
                    </span>
                  </DropdownMenuItem>
                ))
              )}
            </React.Fragment>
          ))}
        </>
      )
    }

    if (users.length === 0) {
      return <div className="px-3 py-3 text-xs text-muted-foreground">No volunteers</div>
    }

    return (
      <>
        <DropdownMenuLabel className="text-muted-foreground text-xs font-bold">
          {title} ({users.length})
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {users.map((u) => (
          <DropdownMenuItem
            key={u.userId}
            onClick={() => onSelectUser?.(u)}
            className="flex flex-col items-start py-1.5 cursor-pointer text-xs"
          >
            <span className="font-medium text-foreground">{u.fullName}</span>
            <span className="text-[11px] text-muted-foreground">
              {userSubtitle ? userSubtitle(u) : u.groupName || 'Individual Volunteer'}
            </span>
          </DropdownMenuItem>
        ))}
      </>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div
          role="button"
          tabIndex={0}
          aria-label={`View ${title} volunteers`}
          className={cn(
            'rounded-xl p-3 flex justify-between items-center gap-3 border min-w-0 cursor-pointer hover:bg-muted/40 transition-all shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-ring select-none',
            cardHighlightClass
          )}
        >
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-xs truncate">{title}</p>
            <p className="text-base font-bold mt-0.5">{countNode ?? count}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <div
              className={cn(
                'rounded-full p-2 w-9 aspect-square flex items-center justify-center shrink-0',
                iconHighlightClass
              )}
            >
              {icon}
            </div>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </div>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-72 w-64 overflow-y-auto">
        {renderDropdownContent()}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export interface ExpandedPlanningViewProps {
  weekStart: string
  weekEnd: string
  weekDates: Array<{ date: string; dayName: string; dayNumber: string; isToday: boolean }>
  regionId?: number
  volunteers: JobBoardVolunteer[]
  candidateTeamLeads: JobBoardVolunteer[]
  groups: JobBoardGroup[]
  teams?: JobBoardTeam[]
  activeLeaderIds?: number[]
  onScheduleWorkScope: (scope: any) => Promise<void> | void
  onAssignToTeam?: (volunteerId: number, targetTeamId: number | null) => Promise<void> | void
  onToggleLeader?: (vol: JobBoardVolunteer) => void
  onCloseExpandedMode: () => void
  onRefresh: () => void
  isLoading?: boolean
}

export function ExpandedPlanningView({
  weekStart,
  weekEnd,
  weekDates,
  regionId,
  volunteers,
  candidateTeamLeads,
  groups,
  teams = [],
  activeLeaderIds = [],
  onScheduleWorkScope,
  onAssignToTeam,
  onToggleLeader,
  onCloseExpandedMode,
  onRefresh,
  isLoading = false,
}: ExpandedPlanningViewProps) {
  const { data: priorityHomes, isLoading: isLoadingPriority, refetch: refetchPriority } =
    usePriorityData(regionId)

  // Filters & State
  const [search, setSearch] = useState('')
  const [includeGoodwill, setIncludeGoodwill] = useState(true)
  const [selectedScopes, setSelectedScopes] = useState<string[]>([])
  const [highlightSkillKey, setHighlightSkillKey] = useState<string | null>(null)
  const [isRosterOpen, setIsRosterOpen] = useState(false)

  // 1. Compute Volunteer Overview breakdowns (similar to GroupParticipantsTab)
  const hasConstructionSkill3Plus = useCallback((v: JobBoardVolunteer) => {
    if ((v.experienceConstruction ?? 0) >= 3) return true
    const s = v.skills || {}
    return (
      (s.drywallFinishing ?? 0) >= 3 ||
      (s.roughCarpentry ?? 0) >= 3 ||
      (s.finishCarpentry ?? 0) >= 3 ||
      (s.flooring ?? 0) >= 3 ||
      (s.woodWorking ?? 0) >= 3 ||
      (s.electrical ?? 0) >= 3 ||
      (s.plumbing ?? 0) >= 3 ||
      (s.hvac ?? 0) >= 3 ||
      (s.roofing ?? 0) >= 3 ||
      (s.heavyEquipment ?? 0) >= 3 ||
      (s.arborist ?? 0) >= 3
    )
  }, [])

  const constructionVolunteers = useMemo(
    () => volunteers.filter(hasConstructionSkill3Plus),
    [volunteers, hasConstructionSkill3Plus]
  )

  const otherSkillsVolunteers = useMemo(() => {
    return volunteers.filter((v) => {
      const s = v.skills || {}
      return (
        (s.manualLabor ?? 0) >= 1 ||
        (s.altMinistries ?? 0) >= 1 ||
        (s.dataManagement ?? 0) >= 1 ||
        (s.storytelling ?? 0) >= 1
      )
    })
  }, [volunteers])

  const potentialLeads = useMemo(() => {
    return volunteers.filter((v) => {
      const isLeadByRole =
        v.role?.toLowerCase().includes('lead') ||
        v.role?.toLowerCase().includes('admin')
      const isLeadByExp = (v.experienceLeadingTeams ?? 0) >= 3
      return isLeadByRole || isLeadByExp
    })
  }, [volunteers])

  const cookingVolunteers = useMemo(() => {
    return volunteers.filter((v) => (v.mealPreparationAbility ?? 0) >= 3)
  }, [volunteers])

  const dietaryVolunteers = useMemo(() => {
    const exclude = ['na', 'n/a', 'none', 'no']
    return volunteers.filter((v) => {
      const dr = (v.dietaryRestrictions || '').trim().toLowerCase()
      return dr.length > 0 && !exclude.includes(dr)
    })
  }, [volunteers])

  const malesVolunteers = useMemo(
    () => volunteers.filter((v) => (v.gender || '').toLowerCase().startsWith('m')),
    [volunteers]
  )
  const femalesVolunteers = useMemo(
    () => volunteers.filter((v) => (v.gender || '').toLowerCase().startsWith('f')),
    [volunteers]
  )

  const onBaseMalesVolunteers = useMemo(
    () => malesVolunteers.filter((v) => v.stayingOnBase !== false),
    [malesVolunteers]
  )
  const onBaseFemalesVolunteers = useMemo(
    () => femalesVolunteers.filter((v) => v.stayingOnBase !== false),
    [femalesVolunteers]
  )

  // 2. Parse & Group Priority Homes by Work Scope
  interface ReadyScopeItem {
    homeId: number
    homeownerName: string
    address: string
    workScope: string
    workScopeDisplay: string
    workScopeId: number | null
    status: string
    isGoodwill: boolean
    priorityRank: number
  }

  const scopesWithItems = useMemo(() => {
    if (!priorityHomes || !Array.isArray(priorityHomes)) return []

    const map = new Map<
      string,
      {
        workScope: string
        workScopeDisplay: string
        workScopeId: number | null
        items: ReadyScopeItem[]
      }
    >()

    let globalRank = 0

    priorityHomes.forEach((entry: any) => {
      // Format A: WorkScopePriorityDisplay from GET /priority
      if (
        entry.priorities &&
        (Array.isArray(entry.priorities.ready) || Array.isArray(entry.priorities.goodwill))
      ) {
        const wsKey = entry.workScope || 'other'
        const wsDisplay = entry.workScopeDisplay || entry.workScope || 'Work Scope'
        const wsId = entry.workScopeId || null

        if (!map.has(wsKey)) {
          map.set(wsKey, {
            workScope: wsKey,
            workScopeDisplay: wsDisplay,
            workScopeId: wsId,
            items: [],
          })
        }
        const col = map.get(wsKey)!

        ;(entry.priorities.ready || []).forEach((item: any) => {
          globalRank++
          col.items.push({
            homeId: item.homeId,
            homeownerName: item.name || `Home #${item.homeId}`,
            address: item.address || '',
            workScope: wsKey,
            workScopeDisplay: wsDisplay,
            workScopeId: wsId,
            status: item.status || 'Ready',
            isGoodwill: false,
            priorityRank: globalRank,
          })
        })

        if (includeGoodwill) {
          ;(entry.priorities.goodwill || []).forEach((item: any) => {
            if (item.status === 'Ready' || !item.status) {
              globalRank++
              col.items.push({
                homeId: item.homeId,
                homeownerName: item.name || `Home #${item.homeId}`,
                address: item.address || '',
                workScope: wsKey,
                workScopeDisplay: wsDisplay,
                workScopeId: wsId,
                status: item.status || 'Ready',
                isGoodwill: true,
                priorityRank: globalRank,
              })
            }
          })
        }
        return
      }

      // Format B: Home entity with homeWorkScopes
      const home = entry
      const homeownerName =
        home.homeowner?.user?.fullname || home.homeowner?.name || `Home #${home.id}`
      const address = home.address || ''

      if (Array.isArray(home.homeWorkScopes)) {
        home.homeWorkScopes.forEach((hws: any) => {
          const status = (hws.status || '').toLowerCase()
          if (
            status === 'ready' ||
            status === 'in_progress' ||
            status === 'in progress' ||
            status === 'pending' ||
            status === 'approved' ||
            !status
          ) {
            const wsKey = hws.workScope?.workScope || hws.workScopeName || 'other'
            const wsDisplay =
              hws.workScope?.workScopeDisplay ||
              hws.workScope?.workScope ||
              hws.workScopeName ||
              'Work Scope'
            const wsId = hws.workScope?.id || null

            if (!map.has(wsKey)) {
              map.set(wsKey, {
                workScope: wsKey,
                workScopeDisplay: wsDisplay,
                workScopeId: wsId,
                items: [],
              })
            }
            const col = map.get(wsKey)!
            globalRank++
            col.items.push({
              homeId: home.id,
              homeownerName,
              address,
              workScope: wsKey,
              workScopeDisplay: wsDisplay,
              workScopeId: wsId,
              status: hws.status || 'Ready',
              isGoodwill: hws.isGoodwill || false,
              priorityRank: globalRank,
            })
          }
        })
      }
    })

    // Return only scopes that have ready work scopes
    return Array.from(map.values()).filter((col) => col.items.length > 0)
  }, [priorityHomes, includeGoodwill])

  // Distinct scope options for multi-select
  const allScopeKeys = useMemo(
    () => scopesWithItems.map((s) => s.workScope),
    [scopesWithItems]
  )

  // Filter columns based on selected scopes and search
  const visibleScopeColumns = useMemo(() => {
    let cols = scopesWithItems

    // Filter by selected scopes if any selected
    if (selectedScopes.length > 0) {
      cols = cols.filter((col) => selectedScopes.includes(col.workScope))
    }

    // Filter by highlighted skill if set
    if (highlightSkillKey) {
      cols = cols.filter((col) => {
        const matches = matchVolunteersForScope(col.workScope, volunteers)
        return matches.length > 0
      })
    }

    // Filter items inside columns based on search
    if (search.trim()) {
      const q = search.toLowerCase()
      cols = cols
        .map((col) => ({
          ...col,
          items: col.items.filter(
            (item) =>
              item.homeownerName.toLowerCase().includes(q) ||
              item.address.toLowerCase().includes(q) ||
              col.workScopeDisplay.toLowerCase().includes(q)
          ),
        }))
        .filter((col) => col.items.length > 0)
    }

    return cols
  }, [scopesWithItems, selectedScopes, highlightSkillKey, search, volunteers])

  const toggleScope = (scopeKey: string) => {
    setSelectedScopes((prev) => {
      if (prev.includes(scopeKey)) {
        return prev.filter((k) => k !== scopeKey)
      }
      return [...prev, scopeKey]
    })
  }

  const selectAllScopes = () => {
    setSelectedScopes([])
  }

  const totalReadyWorkCount = useMemo(
    () => scopesWithItems.reduce((acc, col) => acc + col.items.length, 0),
    [scopesWithItems]
  )

  const activeLeaderIdsSet = useMemo(() => new Set(activeLeaderIds), [activeLeaderIds])

  return (
    <div className="flex flex-col w-full min-h-[700px] bg-background border rounded-xl shadow-xs overflow-hidden">
      {/* 1. Header Toolbar */}
      <div className="p-4 border-b bg-muted/20 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <Button
            variant="outline"
            size="sm"
            onClick={onCloseExpandedMode}
            className="h-8 gap-1.5 text-xs font-semibold shadow-xs"
            title="Return to 6-Day Calendar View"
          >
            <Calendar className="w-3.5 h-3.5" />
            Calendar View
          </Button>

          <div className="h-4 w-px bg-border mx-1" />

          <div>
            <h2 className="text-base font-bold flex items-center gap-2">
              <Layers className="w-4 h-4 text-primary" />
              Initial Planning Workspace
              <Badge variant="secondary" className="text-xs font-medium">
                Week of {weekDates[0]?.dayNumber} – {weekDates[5]?.dayNumber}
              </Badge>
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Multi-Scope Priority Matrix matching available volunteer workforce & skills
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative w-48 lg:w-64">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
              placeholder="Search homes, addresses..."
              className="h-8 pl-8 text-xs bg-background"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Goodwill checkbox */}
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground cursor-pointer bg-background px-2 py-1 rounded-md border h-8 shadow-xs">
            <input
              type="checkbox"
              checked={includeGoodwill}
              onChange={(e) => setIncludeGoodwill(e.target.checked)}
              className="rounded text-primary focus:ring-0 w-3.5 h-3.5"
            />
            <span>Goodwill Scopes</span>
          </label>

          {/* Toggle Volunteer Roster Drawer */}
          <Button
            variant={isRosterOpen ? 'default' : 'outline'}
            size="sm"
            onClick={() => setIsRosterOpen(!isRosterOpen)}
            className="h-8 text-xs gap-1.5"
            title="Toggle Volunteer Roster"
          >
            <Users className="w-3.5 h-3.5" />
            Roster ({volunteers.length})
          </Button>

          {/* Refresh Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              onRefresh()
              refetchPriority()
            }}
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
            title="Refresh priorities & volunteers"
          >
            <RefreshCw className={cn('w-3.5 h-3.5', (isLoading || isLoadingPriority) && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* 2. At-A-Glance Volunteer Summary Banner (All groups on site that week) */}
      <div className="p-4 border-b bg-card space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Volunteer Workforce on Site ({volunteers.length} Active Volunteers)
            </span>
            {groups.length > 0 && (
              <Popover>
                <PopoverTrigger asChild>
                  <Badge
                    variant="outline"
                    className="text-[11px] cursor-pointer hover:bg-muted/80 transition-colors gap-1"
                  >
                    <Users className="w-3 h-3 text-primary" />
                    {groups.length} Visiting Group{groups.length > 1 ? 's' : ''}
                    <ChevronDown className="w-3 h-3 text-muted-foreground" />
                  </Badge>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-3" align="start">
                  <h4 className="text-xs font-bold text-foreground mb-2">
                    Groups On Site This Week
                  </h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {groups.map((g) => (
                      <div
                        key={g.id}
                        className="p-2 rounded-md bg-muted/40 border text-xs space-y-1"
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-foreground">{g.groupName}</span>
                          <Badge variant="secondary" className="text-[10px]">
                            {g.participants} Participants
                          </Badge>
                        </div>
                        {g.leaderName && (
                          <div className="text-[11px] text-muted-foreground">
                            Leader: {g.leaderName}
                          </div>
                        )}
                        <div className="text-[10px] text-muted-foreground">
                          {g.startDate} – {g.endDate}
                        </div>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>

          {highlightSkillKey && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setHighlightSkillKey(null)}
              className="h-6 text-[11px] text-muted-foreground hover:text-foreground gap-1"
            >
              <X className="w-3 h-3" />
              Clear Skill Focus
            </Button>
          )}
        </div>

        {/* 7 Banner Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2.5">
          <VolunteerOverviewCard
            title="Construction Skills"
            count={constructionVolunteers.length}
            icon={<HardHat className="h-4 w-4" />}
            highlight="construction"
            users={constructionVolunteers}
            userSubtitle={(u) => {
              const s = u.skills || {}
              const skillsList: string[] = []
              if ((u.experienceConstruction ?? 0) >= 3)
                skillsList.push(`Gen (${u.experienceConstruction}/5)`)
              if ((s.drywallFinishing ?? 0) >= 3)
                skillsList.push(`Drywall (${s.drywallFinishing}/5)`)
              if ((s.roughCarpentry ?? 0) >= 3)
                skillsList.push(`Carpentry (${s.roughCarpentry}/5)`)
              if ((s.roofing ?? 0) >= 3)
                skillsList.push(`Roofing (${s.roofing}/5)`)
              if ((s.electrical ?? 0) >= 3)
                skillsList.push(`Electrical (${s.electrical}/5)`)
              if ((s.plumbing ?? 0) >= 3)
                skillsList.push(`Plumbing (${s.plumbing}/5)`)
              if ((s.arborist ?? 0) >= 3)
                skillsList.push(`Tree Work (${s.arborist}/5)`)
              return skillsList.join(', ') || u.groupName || ''
            }}
          />

          <VolunteerOverviewCard
            title="Other Skills"
            count={otherSkillsVolunteers.length}
            icon={<Sparkles className="h-4 w-4" />}
            highlight="other"
            users={otherSkillsVolunteers}
            userSubtitle={(u) => {
              const s = u.skills || {}
              const parts: string[] = []
              if ((s.manualLabor ?? 0) >= 1) parts.push(`Manual Labor (${s.manualLabor}/5)`)
              if ((s.altMinistries ?? 0) >= 1) parts.push(`Alt Min (${s.altMinistries}/5)`)
              if ((s.dataManagement ?? 0) >= 1) parts.push(`Data (${s.dataManagement}/5)`)
              if ((s.storytelling ?? 0) >= 1) parts.push(`Social (${s.storytelling}/5)`)
              return parts.join(', ') || u.groupName || ''
            }}
          />

          <VolunteerOverviewCard
            title="Potential Team Leads"
            count={potentialLeads.length}
            icon={<UserCheck className="h-4 w-4" />}
            highlight="construction"
            users={potentialLeads}
            userSubtitle={(u) =>
              `Lead Exp: ${u.experienceLeadingTeams}/5 • ${u.role || 'Volunteer'}`
            }
          />

          <VolunteerOverviewCard
            title="Interested in Cooking"
            count={cookingVolunteers.length}
            icon={<ChefHat className="h-4 w-4" />}
            highlight="info"
            users={cookingVolunteers}
            userSubtitle={(u) => `Cooking Ability: ${u.mealPreparationAbility}/5`}
          />

          <VolunteerOverviewCard
            title="Dietary Restrictions"
            count={dietaryVolunteers.length}
            icon={<UtensilsCrossed className="h-4 w-4" />}
            highlight="info"
            users={dietaryVolunteers}
            userSubtitle={(u) => u.dietaryRestrictions || 'Restricted'}
          />

          <VolunteerOverviewCard
            title="Total Males/Females"
            count={malesVolunteers.length + femalesVolunteers.length}
            countNode={
              <>
                {malesVolunteers.length} <span className="text-muted-foreground font-normal">/</span>{' '}
                {femalesVolunteers.length}
              </>
            }
            icon={<Users className="h-4 w-4" />}
            highlight="neutral"
            users={[...malesVolunteers, ...femalesVolunteers]}
            sections={[
              { label: 'Male', users: malesVolunteers },
              { label: 'Female', users: femalesVolunteers },
            ]}
          />

          <VolunteerOverviewCard
            title="On Base Males/Females"
            count={onBaseMalesVolunteers.length + onBaseFemalesVolunteers.length}
            countNode={
              <>
                {onBaseMalesVolunteers.length}{' '}
                <span className="text-muted-foreground font-normal">/</span>{' '}
                {onBaseFemalesVolunteers.length}
              </>
            }
            icon={<Users className="h-4 w-4" />}
            highlight="neutral"
            users={[...onBaseMalesVolunteers, ...onBaseFemalesVolunteers]}
            sections={[
              { label: 'On Base Male', users: onBaseMalesVolunteers },
              { label: 'On Base Female', users: onBaseFemalesVolunteers },
            ]}
          />
        </div>
      </div>

      {/* 3. Scope Selector Tabs Bar */}
      <div className="px-4 py-2.5 border-b bg-muted/10 flex flex-wrap items-center justify-between gap-2 text-xs">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-semibold text-muted-foreground flex items-center gap-1 mr-1">
            <Filter className="w-3 h-3" /> Scopes:
          </span>

          <Button
            size="sm"
            variant={selectedScopes.length === 0 ? 'default' : 'outline'}
            onClick={selectAllScopes}
            className="h-6 text-[11px] px-2.5 rounded-full font-medium"
          >
            All Scopes ({totalReadyWorkCount})
          </Button>

          {scopesWithItems.map((col) => {
            const isSelected = selectedScopes.includes(col.workScope)
            const matchedVols = matchVolunteersForScope(col.workScope, volunteers)
            const hasQualifiedVols = matchedVols.length > 0

            return (
              <button
                key={col.workScope}
                onClick={() => toggleScope(col.workScope)}
                className={cn(
                  'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium transition-colors border',
                  isSelected
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-input hover:bg-accent'
                )}
              >
                <span>{col.workScopeDisplay}</span>
                <span
                  className={cn(
                    'text-[10px] px-1 rounded-full',
                    isSelected
                      ? 'bg-primary-foreground/20 text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  )}
                >
                  {col.items.length}
                </span>
                {hasQualifiedVols && (
                  <span
                    className={cn(
                      'w-1.5 h-1.5 rounded-full',
                      isSelected ? 'bg-emerald-300' : 'bg-emerald-500'
                    )}
                    title={`${matchedVols.length} volunteers with skills on site`}
                  />
                )}
              </button>
            )
          })}
        </div>

        {/* Skill filter toggle */}
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-muted-foreground">
            Showing {visibleScopeColumns.length} scope column{visibleScopeColumns.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* 4. Main Multi-Scope Columns Area with Optional Roster Drawer */}
      <div className="flex-1 flex overflow-hidden min-h-0 relative">
        {/* Multi-Scope Columns Container */}
        <div className="flex-1 overflow-x-auto p-4 flex gap-4 min-w-0">
          {isLoadingPriority ? (
            <div className="flex-1 flex items-center justify-center py-20 text-xs text-muted-foreground gap-2">
              <RefreshCw className="w-4 h-4 animate-spin" />
              Loading ready work scopes from Priority Matrix...
            </div>
          ) : visibleScopeColumns.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center py-20 text-xs text-muted-foreground">
              <Layers className="w-8 h-8 mb-2 text-muted-foreground/50" />
              <span className="font-semibold text-foreground">No ready work scopes found</span>
              <span className="text-muted-foreground mt-0.5">Try clearing search filters or selecting different scopes above</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearch('')
                  setSelectedScopes([])
                  setHighlightSkillKey(null)
                }}
                className="mt-3 text-xs"
              >
                Reset Filters
              </Button>
            </div>
          ) : (
            visibleScopeColumns.map((col) => {
              const matchedVolunteers = matchVolunteersForScope(col.workScope, volunteers)
              const hasMatches = matchedVolunteers.length > 0

              return (
                <div
                  key={col.workScope}
                  className="w-80 lg:w-88 shrink-0 flex flex-col rounded-xl border bg-muted/30 max-h-full"
                >
                  {/* Column Header */}
                  <div className="p-3 border-b bg-card rounded-t-xl space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold text-sm text-foreground flex items-center gap-1.5">
                        <span>{col.workScopeDisplay}</span>
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                          {col.items.length} Ready
                        </Badge>
                      </h3>
                    </div>

                    {/* Skill Matching Indicator Badge & Popover */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <div
                          role="button"
                          className={cn(
                            'p-2 rounded-lg border text-xs flex items-center justify-between cursor-pointer transition-all',
                            hasMatches
                              ? 'bg-emerald-50/70 border-emerald-300 text-emerald-800 dark:bg-emerald-950/30 dark:border-emerald-800 dark:text-emerald-300 hover:bg-emerald-100/70'
                              : 'bg-amber-50/70 border-amber-300 text-amber-800 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-300 hover:bg-amber-100/70'
                          )}
                        >
                          <div className="flex items-center gap-1.5">
                            {hasMatches ? (
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                            ) : (
                              <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                            )}
                            <span className="font-medium text-[11px]">
                              {hasMatches
                                ? `${matchedVolunteers.length} Skilled Volunteer${matchedVolunteers.length > 1 ? 's' : ''}`
                                : 'No volunteers on site with trade skill'}
                            </span>
                          </div>
                          <ChevronDown className="w-3 h-3 opacity-60" />
                        </div>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 p-3" align="start">
                        <h4 className="text-xs font-bold text-foreground mb-1.5">
                          Volunteers Matching {col.workScopeDisplay}
                        </h4>
                        {hasMatches ? (
                          <div className="space-y-1.5 max-h-52 overflow-y-auto">
                            {matchedVolunteers.map(({ volunteer: v, rating, skillLabel }) => (
                              <div
                                key={v.userId}
                                className="p-1.5 rounded bg-muted/50 border text-xs flex items-center justify-between"
                              >
                                <div>
                                  <div className="font-medium text-foreground">{v.fullName}</div>
                                  <div className="text-[10px] text-muted-foreground">
                                    {v.groupName || 'Individual'}
                                  </div>
                                </div>
                                <Badge
                                  variant="outline"
                                  className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300 font-semibold"
                                >
                                  {skillLabel}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            None of the volunteers on site this week have listed experience (rating ≥ 3) in this scope.
                            Consider assigning a cross-trained team lead or pairing with a general construction crew.
                          </p>
                        )}
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Scope Items List */}
                  <div className="flex-1 overflow-y-auto p-2.5 space-y-2">
                    {col.items.map((item) => (
                      <div
                        key={`${item.homeId}-${item.workScope}-${item.priorityRank}`}
                        draggable
                        onDragStart={(e) => {
                          const payload = { type: 'NEW_WORK_SCOPE' as const, scope: item }
                          setActiveJobBoardDrag(payload)
                          e.dataTransfer.setData('text/plain', JSON.stringify(payload))
                          e.dataTransfer.effectAllowed = 'copy'
                        }}
                        onDragEnd={() => setActiveJobBoardDrag(null)}
                        className={cn(
                          'p-3 rounded-xl border text-xs bg-card hover:border-primary/60 transition-all shadow-xs flex flex-col justify-between gap-2.5 cursor-grab active:cursor-grabbing group',
                          item.isGoodwill && 'border-green-300 bg-green-50/15 dark:bg-green-950/20'
                        )}
                      >
                        <div>
                          <div className="flex items-start justify-between gap-1.5">
                            <div className="flex items-center gap-1 min-w-0">
                              <span className="font-bold text-foreground text-xs truncate">
                                {item.homeownerName}
                              </span>
                            </div>
                            <Badge
                              variant="outline"
                              className="text-[9px] px-1 py-0 bg-muted text-muted-foreground shrink-0 font-medium"
                            >
                              Priority #{item.priorityRank}
                            </Badge>
                          </div>

                          {item.address && (
                            <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1 truncate">
                              <MapPin className="w-3 h-3 text-muted-foreground shrink-0" />
                              <span className="truncate">{item.address}</span>
                            </div>
                          )}

                          {item.isGoodwill && (
                            <span className="text-[10px] text-green-700 font-semibold block mt-1">
                              • Goodwill Scope
                            </span>
                          )}
                        </div>

                        {/* Quick Schedule Button */}
                        <div className="flex items-center justify-between pt-2 border-t text-[10px]">
                          <span className="text-muted-foreground">Ready to assign</span>
                          <Button
                            size="sm"
                            onClick={() => onScheduleWorkScope(item)}
                            className="h-6 text-[11px] px-2.5 bg-indigo-600 hover:bg-indigo-700 text-white gap-1 font-medium shadow-xs"
                          >
                            <Plus className="w-3 h-3" />
                            Schedule on Board
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })
          )}
        </div>

        {/* Optional Side Volunteer Roster Drawer */}
        {isRosterOpen && (
          <div className="w-80 lg:w-96 border-l bg-card flex flex-col h-full shadow-lg z-20 shrink-0">
            <div className="p-3 border-b flex items-center justify-between bg-muted/40">
              <div className="flex items-center gap-1.5">
                <Users className="w-4 h-4 text-primary" />
                <span className="font-bold text-xs text-foreground">
                  Volunteer Workforce ({volunteers.length})
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsRosterOpen(false)}
                className="h-6 w-6"
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {volunteers.map((vol) => {
                const isLeader = activeLeaderIdsSet.has(vol.userId)

                return (
                  <div
                    key={vol.userId}
                    className={cn(
                      'p-2.5 rounded-lg border text-xs bg-background space-y-1.5 shadow-xs',
                      isLeader && 'border-emerald-300 bg-emerald-50/15'
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-foreground truncate">
                          {vol.fullName}
                        </div>
                        <div className="text-[10px] text-muted-foreground truncate">
                          {vol.groupName || 'Individual'}
                        </div>
                      </div>
                      {onToggleLeader && (
                        <Button
                          size="sm"
                          variant={isLeader ? 'outline' : 'default'}
                          onClick={() => onToggleLeader(vol)}
                          className={cn(
                            'h-5 text-[10px] px-1.5 gap-0.5',
                            isLeader
                              ? 'border-emerald-400 text-emerald-700 bg-emerald-50'
                              : 'bg-primary/10 text-primary hover:bg-primary hover:text-white'
                          )}
                        >
                          {isLeader ? (
                            <>
                              <Check className="w-2.5 h-2.5" /> Lead on Board
                            </>
                          ) : (
                            <>
                              <Plus className="w-2.5 h-2.5" /> Set Lead
                            </>
                          )}
                        </Button>
                      )}
                    </div>

                    {teams.length > 0 && onAssignToTeam && (
                      <div className="flex items-center justify-between text-[10px] bg-muted/40 px-1.5 py-1 rounded">
                        <span className="text-muted-foreground">Team:</span>
                        <select
                          value={vol.teamId ? String(vol.teamId) : ''}
                          onChange={(e) => {
                            const val = e.target.value ? Number(e.target.value) : null
                            onAssignToTeam(vol.userId, val)
                          }}
                          className="h-5 text-[10px] rounded border bg-background px-1 max-w-[150px] truncate"
                        >
                          <option value="">No Team Assigned</option>
                          {teams.map((t) => (
                            <option key={t.id} value={String(t.id)}>
                              {t.teamName}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
